import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ProtectContext } from "../../context/protect_context";
import { IMAGE_BASE_URL } from "../../utils/image_url";
import { setPageTitle } from "../../utils/page_title";
import { ImageWithSkeleton } from "../common/ImageWithSkeleton";
import { REACT_APP_API_BASE_URL } from "../../services/api";
import type {
  Page,
  Section,
  RegularData,
} from "../../types";
import {
  isRegularData,
} from "../../types";

const DetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data } = React.useContext(ProtectContext);
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);

  // Find page by slug param: match href /pages/:slug, /:slug, or slug
  const findPageByHref = (pages: Page[], slugParam: string): Page | null => {
    if (!slugParam) return null;
    const candidates = [
      `/pages/${slugParam}`,
      `/${slugParam}`,
      slugParam,
    ];
    for (const page of pages) {
      if (page.href && candidates.includes(page.href)) {
        return page;
      }
      if (page.pages && page.pages.length > 0) {
        const found = findPageByHref(page.pages, slugParam);
        if (found) return found;
      }
    }
    return null;
  };

  const pageData = data?.datas ? findPageByHref(data.datas, slug || "") : null;

  // Extract slug from pageData href or use slug param
  const pageSlug = pageData?.href?.replace("/pages/", "").replace("/", "") || slug || "";

  // Extract YouTube video ID from URL
  const extractVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Get embed URL from video URL
  const getEmbedUrl = (url: string): string | null => {
    const videoId = extractVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  // Navigate to next video
  const nextVideo = () => {
    if (videoUrls.length > 0) {
      setCurrentVideoIndex((prev) => (prev + 1) % videoUrls.length);
    }
  };

  // Navigate to previous video
  const prevVideo = () => {
    if (videoUrls.length > 0) {
      setCurrentVideoIndex((prev) => (prev - 1 + videoUrls.length) % videoUrls.length);
    }
  };

  // Fetch video URLs
  useEffect(() => {
    const fetchVideos = async () => {
      if (!pageSlug) return;
      
      setIsLoadingVideos(true);
      try {
        const response = await fetch(`${REACT_APP_API_BASE_URL}/pages/${pageSlug}/videos`);
        const result = await response.json();
        
        if (result.success && Array.isArray(result.data)) {
          setVideoUrls(result.data);
          setCurrentVideoIndex(0);
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setIsLoadingVideos(false);
      }
    };

    fetchVideos();
  }, [pageSlug]);

  // Set page title
  React.useEffect(() => {
    setPageTitle(pageData, data);
  }, [pageData, data]);

  if (!pageData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center space-y-6 px-4">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-100 to-orange-100">
            <svg
              className="h-10 w-10 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-4xl font-extrabold text-slate-900 lg:text-5xl">
            Хуудас олдсонгүй
          </h1>
          <p className="text-lg text-slate-600">
            Уучлаарай, таны хайж буй хуудас олдсонгүй
          </p>
          <Link
            to="/"
            className="group inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-orange-500 px-8 py-4 text-base font-bold text-white shadow-lg shadow-red-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-red-500/60"
          >
            Нүүр хуудас руу буцах
            <svg
              className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Modern Header Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 lg:py-24">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-gradient-to-br from-red-500/20 to-orange-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-4xl px-4 text-center lg:px-8">
          <h1 className="mb-6 text-4xl font-extrabold leading-tight text-white drop-shadow-2xl lg:text-5xl xl:text-6xl">
            {pageData.title}
          </h1>
          {pageData.desc && (
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-300 lg:text-xl">
              {pageData.desc}
            </p>
          )}
        </div>
      </section>

      {/* Video Section - Modern Design with Carousel */}
      {videoUrls.length > 0 && (
        <section className="relative -mt-12 px-4 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">
            <div className="group relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-500 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
              <div className="relative w-full overflow-hidden rounded-3xl bg-slate-900 pt-[56.25%]">
                {isLoadingVideos ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
                  </div>
                ) : (
                  <>
                    {getEmbedUrl(videoUrls[currentVideoIndex]) && (
                      <iframe
                        key={currentVideoIndex}
                        className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-105"
                        src={getEmbedUrl(videoUrls[currentVideoIndex]) || ""}
                        title={`Video ${currentVideoIndex + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    )}
                    
                    {/* Carousel Navigation - Only show if more than 1 video */}
                    {videoUrls.length > 1 && (
                      <>
                        {/* Previous Button */}
                        <button
                          onClick={prevVideo}
                          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white transition-all duration-300 hover:bg-black/70 hover:scale-110"
                          aria-label="Previous video"
                        >
                          <svg
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Next Button */}
                        <button
                          onClick={nextVideo}
                          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white transition-all duration-300 hover:bg-black/70 hover:scale-110"
                          aria-label="Next video"
                        >
                          <svg
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        {/* Video Indicators */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                          {videoUrls.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentVideoIndex(index)}
                              className={`h-2 rounded-full transition-all duration-300 ${
                                index === currentVideoIndex
                                  ? "w-8 bg-white"
                                  : "w-2 bg-white/50 hover:bg-white/75"
                              }`}
                              aria-label={`Go to video ${index + 1}`}
                            />
                          ))}
                        </div>

                        {/* Video Counter */}
                        <div className="absolute top-4 right-4 z-10 rounded-full bg-black/50 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white">
                          {currentVideoIndex + 1} / {videoUrls.length}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {/* Decorative gradient overlay */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 pointer-events-none" />
            </div>
          </div>
        </section>
      )}
      {/* Content Sections */}
      {pageData.sections?.map((section: Section, sectionIndex: number) => {
        const sId = Number(section.sectionId) ?? 4;
        if (sId === 4) {
          const sectionLength = section.datas?.length || 0;
          const firstItem =
            sectionLength > 3
              ? (section.datas?.[0] as RegularData | undefined)
              : null;
          const remainingItems =
            sectionLength > 3
              ? section.datas?.slice(1).filter(isRegularData) || []
              : section.datas?.filter(isRegularData) || [];
          const remainingCount = remainingItems.length;

          return (
            <section
              key={sectionIndex}
              className={`relative py-16 lg:py-24 ${
                section.backgroundColor === "#f6f7f9"
                  ? "bg-gradient-to-br from-slate-50 to-white"
                  : "bg-white"
              }`}
            >
              <div className="mx-auto w-full max-w-7xl px-4 lg:px-8">
                {/* First item - Modern Card Design */}
                {firstItem && (
                  <div className="group mb-16 flex flex-col items-center gap-8 overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl lg:flex-row lg:items-center lg:justify-center lg:gap-12 lg:p-12 group-hover:from-red-50/50 group-hover:to-orange-50/50  hover:border-red-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 " />

                    {firstItem.icon && (
                      <div className="relative z-10 flex-shrink-0">
                        <div className="relative">
                          <ImageWithSkeleton
                            src={`${IMAGE_BASE_URL}${firstItem.icon}`}
                            alt={firstItem.title}
                            className="h-[200px] w-[280px] object-contain transition-transform duration-500 group-hover:scale-110 sm:h-[250px] sm:w-[350px] lg:h-[400px] lg:w-[400px]"
                          />
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/0 to-orange-500/0 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-30" />
                        </div>
                      </div>
                    )}

                    <div className="relative z-10 flex flex-col items-center gap-4 text-center lg:items-start lg:text-left lg:max-w-none">
                      {firstItem.title && (
                        <h4 className="text-2xl font-extrabold leading-tight text-slate-900 sm:text-3xl lg:text-4xl">
                          {firstItem.title}
                        </h4>
                      )}
                      {firstItem.desc && (
                        <div
                          className="prose prose-lg max-w-none text-slate-600 prose-headings:text-slate-900 prose-p:text-slate-600"
                          dangerouslySetInnerHTML={{
                            __html: firstItem.desc,
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Remaining items - Modern Grid */}
                {remainingCount > 0 && (
                  <div className="flex justify-center">
                    <div
                      className={`grid gap-6 text-center ${
                        remainingCount === 5
                          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
                          : remainingCount === 4
                          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4"
                          : remainingCount === 3
                          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                          : remainingCount === 2
                          ? "grid-cols-1 sm:grid-cols-2"
                          : "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3"
                      }`}
                    >
                      {remainingItems
                        .filter(isRegularData)
                        .map((item: RegularData, itemIndex: number) => (
                          <div
                            key={itemIndex}
                            className="group relative flex flex-col items-center gap-4 overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:border-red-300 hover:shadow-2xl"
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 " />

                            {item.icon && (
                              <div className="relative z-10 transition-transform duration-500 group-hover:scale-110">
                                <ImageWithSkeleton
                                  src={`${IMAGE_BASE_URL}${item.icon}`}
                                  alt={item.title}
                                  className="h-32 w-32 object-contain sm:h-36 sm:w-36 lg:h-[200px] lg:w-[200px]"
                                />
                              </div>
                            )}

                            <div className="relative z-10 space-y-2">
                              {item.title && (
                                <h4 className="text-base font-bold text-slate-900 lg:text-lg">
                                  {item.title}
                                </h4>
                              )}
                              {item.desc && (
                                <div
                                  className="prose prose-sm max-w-none text-slate-600"
                                  dangerouslySetInnerHTML={{
                                    __html: item.desc,
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        }
        if (sId === 5) {
          return (
            <section
              key={sectionIndex}
              className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-white py-16 lg:py-24"
            >
              <div className="mx-auto w-full max-w-7xl px-4 lg:px-8">
                <div className="grid gap-6 md:grid-cols-2">
                  {(section.datas??[]).map((related: any, index: number) => (
                      <div
                        key={index}
                        className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-300 hover:shadow-2xl"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 group-hover:from-red-50/50 group-hover:to-orange-50/50" />

                        <div className="relative z-10">
                          {related.title && (
                            <h4 className="mb-6 text-2xl font-bold text-slate-900">
                              {related.title}
                            </h4>
                          )}
                          {related.link && related.buttinTitle && (
                            <Link
                              to={related.link}
                              className="group/link inline-flex items-center gap-2 text-base font-bold text-red-600 transition-all duration-300 hover:gap-4 hover:text-red-700"
                              onClick={() =>
                                window.scrollTo({ top: 0, behavior: "smooth" })
                              }
                            >
                              {related.buttinTitle}
                              <svg
                                className="h-5 w-5 transition-transform duration-300 group-hover/link:translate-x-1"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                                />
                              </svg>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>
          );
        }

        // Section 6: Burn degree information - Modern Alternating Layout
        if (sId === 6) {
          return (
            <section
              key={sectionIndex}
              className={`relative py-16 lg:py-24 ${
                section.backgroundColor === "#f6f7f9"
                  ? "bg-gradient-to-br from-slate-50 to-white"
                  : "bg-white"
              }`}
            >
              <div className="mx-auto w-full max-w-7xl px-4 lg:px-16">
                <div className="space-y-24">
                  {(section?.datas ?? []).map((item: any, itemIndex: number) => {
                      const isEven = itemIndex % 2 === 0;
                      return (
                        <div
                          key={itemIndex}
                          className="group flex flex-col items-center gap-12 lg:flex-row lg:justify-center"
                        >
                          {/* Text content */}
                          <div
                            className={`flex-1 w-full lg:w-1/2 ${
                              isEven ? "lg:order-2" : "lg:order-1"
                            }`}
                          >
                            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-all duration-500 group-hover:-translate-y-1 hover:shadow-2xl lg:p-10">
                              <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 group-hover:from-red-50/30 group-hover:to-orange-50/30" />

                              <div className="relative z-10 space-y-4">
                                {item.title && (
                                  <h4 className="text-xl font-extrabold text-slate-900 lg:text-2xl">
                                    {item.title}
                                  </h4>
                                )}
                                {item.desc && (
                                  <div
                                    className="prose prose-lg max-w-none text-slate-600"
                                    dangerouslySetInnerHTML={{
                                      __html: item.desc,
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Icon/Illustration */}
                          {item.icon && (
                            <div
                              className={`flex-1 w-full lg:w-1/2 flex justify-center ${
                                isEven
                                  ? "lg:justify-start lg:order-1"
                                  : "lg:justify-end lg:order-2"
                              }`}
                            >
                              <div className="relative">
                                <ImageWithSkeleton
                                  src={`${IMAGE_BASE_URL}${item.icon}`}
                                  alt={item.title}
                                  className="h-auto w-full max-w-md object-contain transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-red-500/0 to-orange-500/0 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </section>
          );
        }

        // Section 7: What3words info - Modern Layout
        if (sId === 7) {
          return (
            <section
              key={sectionIndex}
              className={`relative py-16 lg:py-24 ${
                section.backgroundColor === "#f6f7f9"
                  ? "bg-gradient-to-br from-slate-50 to-white"
                  : "bg-white"
              }`}
            >
              <div className="mx-auto w-full max-w-7xl px-4 lg:px-8">
                {(section?.datas ?? []).map((item: any, itemIndex: number) => (
                    <div
                      key={itemIndex}
                      className="group flex flex-col items-center gap-12 lg:flex-row"
                    >
                      {/* Illustration */}
                      {item.icon && (
                        <div className="flex-shrink-0 w-full lg:w-1/2 flex justify-center">
                          <div className="relative">
                            <ImageWithSkeleton
                              src={`${IMAGE_BASE_URL}${item.icon}`}
                              alt={item.title}
                              className="h-auto w-full max-w-md object-contain transition-transform duration-700 group-hover:scale-105 sm:max-w-lg lg:max-w-xl"
                            />
                            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/0 to-purple-500/0 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20" />
                          </div>
                        </div>
                      )}

                      {/* Text content */}
                      <div className="w-full lg:w-1/2">
                        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-all duration-500 group-hover:-translate-y-1 hover:shadow-2xl lg:p-10">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-purple-50/0 transition-all duration-500 group-hover:from-blue-50/30 group-hover:to-purple-50/30" />

                          <div className="relative z-10 space-y-6 text-center lg:text-left">
                            {item.title && (
                              <h3 className="text-3xl font-extrabold text-slate-900 lg:text-4xl">
                                {item.title}
                              </h3>
                            )}
                            {item.desc && (
                              <div
                                className="prose prose-lg max-w-none text-slate-600"
                                dangerouslySetInnerHTML={{ __html: item.desc }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          );
        }

        // Section 8: Image display - Modern Layout
        if (sId === 8) {
          return <div></div>;
          // return (
          //   <section
          //     key={sectionIndex}
          //     className={`relative py-16 lg:py-24 ${
          //       section.backgroundColor === "#f6f7f9"
          //         ? "bg-gradient-to-br from-slate-50 to-white"
          //         : "bg-white"
          //     }`}
          //   >
          //     <div className="mx-auto w-full max-w-7xl px-4 lg:px-8">
          //       {(section.datas ?? []).map((item: any, itemIndex: number) => (
          //           <div
          //             key={itemIndex}
          //             className="group flex flex-col items-center gap-8 lg:flex-row lg:gap-12"
          //           >
          //             {/* Image */}
          //             {item.image && (
          //               <div className="flex-shrink-0 w-full lg:w-1/2 flex justify-center">
          //                 <div className="relative overflow-hidden rounded-3xl shadow-2xl transition-transform duration-700 group-hover:scale-105">
          //                   <ImageWithSkeleton
          //                     src={`${IMAGE_BASE_URL}${item.image}`}
          //                     alt=""
          //                     className="h-auto w-full max-w-md object-contain sm:max-w-lg lg:max-w-xl"
          //                   />
          //                 </div>
          //               </div>
          //             )}

          //             {/* Icon */}
          //             {item.icon && (
          //               <div className="w-full lg:w-1/2 flex justify-center lg:justify-start">
          //                 <div className="relative">
          //                   <ImageWithSkeleton
          //                     src={`${IMAGE_BASE_URL}${item.icon}`}
          //                     alt=""
          //                     className="h-auto w-full max-w-md object-contain transition-transform duration-700 group-hover:scale-105 sm:max-w-lg lg:max-w-xl"
          //                   />
          //                   <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-red-500/0 to-orange-500/0 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20" />
          //                 </div>
          //               </div>
          //             )}
          //           </div>
          //         ))}
          //     </div>
          //   </section>
          // );
        }

        // Return null for unrecognized section types
        return null;
      })}
    </div>
  );
};

export default DetailPage;
